import { Box, Text } from "@chakra-ui/react";
import { Check } from "lucide-react";
import { ONBOARDING_STEPS } from "./onboarding-steps";

type StepState = "inactive" | "active" | "completed" | "skipped";

interface OnboardingSidebarProps {
  currentStep: string;
  completedSteps: Set<string>;
  skippedSteps: Set<string>;
  onStepClick: (stepId: string) => void;
}

type GetStepStateArgs = {
  stepId: string;
  currentStep: string;
  completedSteps: Set<string>;
  skippedSteps: Set<string>;
};

function getStepState({ stepId, currentStep, completedSteps, skippedSteps }: GetStepStateArgs): StepState {
  const currentIndex = ONBOARDING_STEPS.findIndex((step) => step.id === currentStep);
  const stepIndex = ONBOARDING_STEPS.findIndex((step) => step.id === stepId);

  if (stepId === currentStep) return "active";
  if (stepId === "willkommen" && currentStep !== "willkommen") return "completed";

  // Never show future steps as completed (even if data exists already).
  if (stepIndex > currentIndex) return "inactive";

  if (skippedSteps.has(stepId)) return "skipped";
  if (completedSteps.has(stepId)) return "completed";
  return "inactive";
}

const OnboardingSidebar = ({ currentStep, completedSteps, skippedSteps, onStepClick }: OnboardingSidebarProps) => {
  return (
    <Box
      color="white"
      padding="1em .75em"
      position="fixed"
      overflowX="hidden"
      top="0"
      left="0"
      bottom="0"
      height="100%"
      zIndex="10"
      className="w-[270px] bg-gradient-to-tr from-sky-400 to-indigo-800"
    >
      <Box className="flex flex-col gap-12 h-full min-h-0">
        <Box className="flex text-white items-center h-16 p-2 shrink-0">
          <img
            src="/logo.svg"
            alt="Kandidat.io"
            height="42px"
            width="42px"
            style={{ maxHeight: "50px", maxWidth: "50px", marginRight: "10px" }}
          />
          <img
            src="/logo-full.svg"
            alt="Kandidat.io"
            height="42px"
            width="150px"
            style={{ maxHeight: "50px", maxWidth: "150px", marginRight: "10px" }}
          />
        </Box>

        <Box className="flex flex-col flex-1 min-h-0 overflow-y-auto">
          <nav className="flex flex-col gap-4">
            {ONBOARDING_STEPS.map((step) => {
              const state = getStepState({ stepId: step.id, currentStep, completedSteps, skippedSteps });
              const isClickable = state === "active" || state === "completed" || state === "skipped";
              const isActive = state === "active";

              return (
                <Box
                  key={step.id}
                  as="button"
                  type="button"
                  onClick={() => isClickable && onStepClick(step.id)}
                  disabled={!isClickable}
                  width="100%"
                  textAlign="left"
                  cursor={isClickable ? "pointer" : "default"}
                  _hover={isClickable ? { background: "rgba(249,250,252, 0.2)" } : { background: "transparent" }}
                  background={isActive ? "rgba(249,250,252, 0.2)" : ""}
                  color={isActive ? "yellow.100" : "white"}
                  padding="0.5em .875em"
                  borderRadius="8px"
                  opacity={isClickable ? 1 : 0.55}
                  transition="background 0.15s ease"
                >
                  <Box display="flex" gap="1em" className="items-center">
                    <Box height="1.75em" width="1.75em" className="flex items-center justify-center shrink-0">
                      <StepIndicator state={state} stepNumber={step.stepNumber} />
                    </Box>
                    <Box width="100%" minWidth={0}>
                      <Text className="whitespace-normal leading-snug text-[13px] font-medium">{step.label}</Text>
                      <Text
                        className="whitespace-normal leading-snug text-[11px] mt-0.5"
                        color={isActive ? "yellow.100" : "whiteAlpha.800"}
                        opacity={isActive ? 0.9 : 0.85}
                      >
                        {step.description}
                      </Text>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </nav>
        </Box>
      </Box>
    </Box>
  );
};

const StepIndicator = ({ state, stepNumber }: { state: StepState; stepNumber: number | null }) => {
  const label = stepNumber ?? "";

  if (state === "completed") {
    return (
      <Box
        flexShrink={0}
        w="1.75em"
        h="1.75em"
        borderRadius="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
        className="bg-success"
      >
        <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
      </Box>
    );
  }

  if (state === "skipped") {
    return (
      <Box
        flexShrink={0}
        w="1.75em"
        h="1.75em"
        borderRadius="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="rgba(255,255,255,0.12)"
        border="1px solid rgba(251, 191, 36, 0.9)"
      >
        <Text fontSize="xs" fontWeight="bold" color="rgba(251, 191, 36, 0.95)">
          –
        </Text>
      </Box>
    );
  }

  if (state === "active") {
    return (
      <Box
        flexShrink={0}
        w="1.75em"
        h="1.75em"
        borderRadius="full"
        bg="rgba(249,250,252, 0.25)"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text fontSize="xs" fontWeight="bold" color="yellow.100">
          {label}
        </Text>
      </Box>
    );
  }

  return (
    <Box
      flexShrink={0}
      w="1.75em"
      h="1.75em"
      borderRadius="full"
      bg="rgba(255,255,255,0.2)"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Text fontSize="xs" fontWeight="semibold" color="whiteAlpha.900">
        {label}
      </Text>
    </Box>
  );
};

export default OnboardingSidebar;
